import React, { useEffect, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { FaFileWord } from 'react-icons/fa';
import { createApiClient } from '../../api/client';
import PickerModal from '../components/PickerModal';
import { LANGUAGES } from '../data/languages';
import { extractDocxText } from '../utils/docx';
import { LEGACY_FONTS, convertLegacyToUnicode, isLegacyFontSelected } from '../../utils/legacyFontConverter';
import '../mobile.css';

const formatDateDDMMYYYY = (isoDate) => {
  if (!isoDate) return '';
  const [year, month, day] = isoDate.split('-');
  return `${day}/${month}/${year}`;
};

const NewStatement = ({ API_URL }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const fileInputRef = useRef(null);
  const id = location.state?.id;

  const [witnessName, setWitnessName] = useState('');
  const [selectedDate, setSelectedDate] = useState('');
  const [language, setLanguage] = useState('English');
  const [statement, setStatement] = useState('');
  const [legacyFont, setLegacyFont] = useState(LEGACY_FONTS[0]);
  // Holds the last raw (un-converted) statement text - either straight from
  // the Word file(s) or straight from the user's typing/pasting - so
  // switching the Legacy Font picker re-converts from that original source
  // instead of re-running a conversion on text that may already be
  // converted once.
  const rawStatementRef = useRef('');
  const [showLanguagePicker, setShowLanguagePicker] = useState(false);
  const [showLegacyFontPicker, setShowLegacyFontPicker] = useState(false);
  const [isParsingFile, setIsParsingFile] = useState(false);
  const [loading, setLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');

  const MAX_WORD_COUNT = 1000;
  const countWords = (text) => {
    const trimmed = (text || '').trim();
    return trimmed ? trimmed.split(/\s+/).length : 0;
  };
  const statementWordCount = countWords(statement);
  const isOverWordLimit = statementWordCount > MAX_WORD_COUNT;
  const [showWordLimitPopup, setShowWordLimitPopup] = useState(false);
  const wasOverWordLimitRef = useRef(false);

  useEffect(() => {
    if (isOverWordLimit && !wasOverWordLimitRef.current) {
      setShowWordLimitPopup(true);
    }
    wasOverWordLimitRef.current = isOverWordLimit;
  }, [isOverWordLimit]);

  const handleWordFileSelected = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    setIsParsingFile(true);
    setErrorMessage('');

    try {
      const text = await extractDocxText(file);
      const newRaw = rawStatementRef.current ? `${rawStatementRef.current}\n${text}` : text;
      rawStatementRef.current = newRaw;
      setStatement(isLegacyFontSelected(legacyFont) ? convertLegacyToUnicode(newRaw, legacyFont) : newRaw);
    } catch (err) {
      setErrorMessage(err.message || 'Failed to read the Word document.');
    } finally {
      setIsParsingFile(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  // Converts pasted text on the way in when a legacy font is selected - the
  // picker-driven reconversion only fires on a font *change*, so without
  // this a paste (e.g. copying a paragraph out of an old Kruti Dev document)
  // after the font was already selected would silently stay unconverted.
  const handleStatementPaste = (e) => {
    const pasted = (e.clipboardData || window.clipboardData).getData('text');
    if (!pasted) return;
    e.preventDefault();

    const textarea = e.target;
    const selectionStart = textarea.selectionStart;
    const selectionEnd = textarea.selectionEnd;

    const convertedPasted = isLegacyFontSelected(legacyFont) ? convertLegacyToUnicode(pasted, legacyFont) : pasted;
    const newDisplayed = statement.slice(0, selectionStart) + convertedPasted + statement.slice(selectionEnd);

    // Best-effort: mirror the same splice into the raw baseline using the
    // original (un-converted) pasted text, so a later font switch still
    // reconverts from the true source. Exact when raw/displayed are still
    // aligned (nothing converted yet, or pasting at the end); approximate
    // otherwise, since conversion can change text length.
    const raw = rawStatementRef.current;
    const rawStart = Math.min(selectionStart, raw.length);
    const rawEnd = Math.min(selectionEnd, raw.length);
    rawStatementRef.current = raw.slice(0, rawStart) + pasted + raw.slice(rawEnd);

    setStatement(newDisplayed);

    const newCursorPos = selectionStart + convertedPasted.length;
    setTimeout(() => textarea.setSelectionRange(newCursorPos, newCursorPos), 0);
  };

  const isDisabled = loading || !witnessName || !selectedDate || !statement.trim() || isOverWordLimit;

  const handleSubmit = async () => {
    setLoading(true);
    setErrorMessage('');

    try {
      const form = new FormData();
      form.append('id', id);
      form.append('language', language);
      form.append('statement', `Name: ${witnessName}\nInterrogation Date: ${formatDateDDMMYYYY(selectedDate)}\n${statement}`);

      const api = createApiClient(API_URL);
      await api.post('/fir/statement', form);
      navigate(-1);
    } catch {
      setErrorMessage('Failed to upload statement. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  if (!id) return <p style={{ textAlign: 'center', fontWeight: 700 }}>No case selected.</p>;

  return (
    <div>
      <h2 style={{ fontSize: '20px', fontWeight: 700, color: '#094268', marginBottom: '15px' }}>Analyze Statement</h2>

      <label style={{ display: 'block', fontSize: '13px', fontWeight: 600, color: '#094268', marginBottom: '6px' }}>Witness Name</label>
      <input
        placeholder="Witness Name"
        value={witnessName}
        onChange={(e) => setWitnessName(e.target.value)}
        style={{ width: '100%', boxSizing: 'border-box', borderWidth: 1, borderStyle: 'solid', borderColor: '#ccc', borderRadius: '8px', padding: '12px', marginBottom: '12px' }}
      />

      <label style={{ display: 'block', fontSize: '13px', fontWeight: 600, color: '#094268', marginBottom: '6px' }}>Statement Date</label>
      <input
        type="date"
        value={selectedDate}
        onChange={(e) => setSelectedDate(e.target.value)}
        style={{ width: '100%', minWidth: 0, maxWidth: '100%', boxSizing: 'border-box', height: '40px', display: 'block', WebkitAppearance: 'none', appearance: 'none', borderWidth: 1, borderStyle: 'solid', borderColor: '#ccc', borderRadius: '8px', padding: '12px', marginBottom: '12px', background: '#fff' }}
      />

      <label style={{ display: 'block', fontSize: '13px', fontWeight: 600, color: '#094268', marginBottom: '6px' }}>Statement Language</label>
      <div
        onClick={() => setShowLanguagePicker(true)}
        style={{ display: 'flex', justifyContent: 'space-between', border: '1px solid #ccc', borderRadius: '8px', padding: '12px', marginBottom: '12px', cursor: 'pointer' }}
      >
        <span>{language}</span>
        <span style={{ color: '#0A426A', fontWeight: 600 }}>Select</span>
      </div>

      <label style={{ display: 'block', fontSize: '13px', fontWeight: 600, color: '#094268', marginBottom: '6px' }}>Legacy Font</label>
      <div
        onClick={() => setShowLegacyFontPicker(true)}
        style={{ display: 'flex', justifyContent: 'space-between', border: '1px solid #ccc', borderRadius: '8px', padding: '12px', marginBottom: '12px', cursor: 'pointer' }}
      >
        <span style={{ color: legacyFont !== LEGACY_FONTS[0] ? '#000' : '#999' }}>{legacyFont}</span>
        <span style={{ color: '#0A426A', fontWeight: 600 }}>Select</span>
      </div>

      <label style={{ display: 'block', fontSize: '13px', fontWeight: 600, color: '#094268', marginBottom: '6px' }}>Statement</label>
      <div style={{ position: 'relative' }}>
        <textarea
          placeholder="Upload statement from Word, or type/paste the statement here."
          value={statement}
          onChange={(e) => {
            setStatement(e.target.value);
            rawStatementRef.current = e.target.value;
          }}
          onPaste={handleStatementPaste}
          disabled={isParsingFile}
          rows={10}
          style={{ width: '100%', boxSizing: 'border-box', borderWidth: 1, borderStyle: 'solid', borderColor: '#ccc', borderRadius: '8px', padding: '12px', marginBottom: '12px', fontFamily: 'inherit', fontSize: '14px', opacity: isParsingFile ? 0.5 : 1 }}
        />

        {isParsingFile && (
          <div style={{ position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'rgba(255,255,255,0.6)' }}>
            <div style={{ textAlign: 'center' }}>
              <div className="m-spinner" style={{ margin: '0 auto', borderTopColor: '#094268' }} />
              <p style={{ marginTop: '8px', fontWeight: 600 }}>Reading Word document...</p>
            </div>
          </div>
        )}
      </div>

      <p style={{ marginTop: '-6px', marginBottom: '12px', fontSize: '13px', textAlign: 'right', color: isOverWordLimit ? '#D32F2F' : '#666' }}>
        {statementWordCount} / {MAX_WORD_COUNT} words
      </p>

      <input ref={fileInputRef} type="file" accept=".docx" onChange={handleWordFileSelected} style={{ display: 'none' }} />
      <button
        type="button"
        onClick={() => fileInputRef.current?.click()}
        disabled={isParsingFile || loading}
        className="m-pill-btn"
        style={{ color: '#2B579A', fontWeight: 600, gap: '8px', marginBottom: '12px' }}
      >
        <FaFileWord size={16} /> Word
      </button>

      <button onClick={handleSubmit} disabled={isDisabled} className="m-button-primary">
        {loading ? <div className="m-spinner" style={{ margin: '0 auto' }} /> : 'Submit'}
      </button>

      {showLanguagePicker && (
        <PickerModal title="Language" options={LANGUAGES} selected={language} onSelect={setLanguage} onClose={() => setShowLanguagePicker(false)} />
      )}

      {showLegacyFontPicker && (
        <PickerModal
          title="Legacy Font"
          options={LEGACY_FONTS}
          selected={legacyFont}
          onSelect={(newFont) => {
            setLegacyFont(newFont);
            if (isLegacyFontSelected(newFont) && rawStatementRef.current.trim() !== '') {
              setStatement(convertLegacyToUnicode(rawStatementRef.current, newFont));
            } else if (newFont === LEGACY_FONTS[0]) {
              // Not Applicable - restore the original text as first pasted/extracted, undoing any conversion.
              setStatement(rawStatementRef.current);
            }
          }}
          onClose={() => setShowLegacyFontPicker(false)}
        />
      )}

      {errorMessage && (
        <div className="m-popup-overlay">
          <div className="m-popup-container">
            <h3>Error</h3>
            <p>{errorMessage}</p>
            <button className="m-button-primary" onClick={() => setErrorMessage('')}>OK</button>
          </div>
        </div>
      )}

      {showWordLimitPopup && (
        <div className="m-popup-overlay">
          <div className="m-popup-container">
            <h3>Word Limit Reached</h3>
            <p>The free version supports a statement of up to {MAX_WORD_COUNT} words. Please shorten the text to continue, or contact us for a paid plan with a higher limit.</p>
            <button className="m-button-primary" onClick={() => setShowWordLimitPopup(false)}>OK</button>
          </div>
        </div>
      )}
    </div>
  );
};

export default NewStatement;
